import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { IUser } from '@core/interfaces/user.interface';
import { TableStateService } from '@core/services/table-state.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdminUserFeedbackDetailsComponent } from '@pages/admin/admin-user-feedback/admin-user-feedback-details/admin-user-feedback-details.component';
import { AdminUserGroupStore } from '@pages/admin/admin-user-group/admin-user-group.store';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { EBugReportStatus, EBugReportType, IBugReport } from '@shared/components/bug-report/bug-report.interface';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import { IDatatableConfiguration, TDatatableColumn } from '@shared/components/datatable/datatable.interface';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogService } from '@shared/components/side-dialog/side-dialog.service';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AdminUserFeedbackStore } from './admin-user-feedback.store';

@Component({
  imports: [
    FormsModule,
    ContentContainerComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    DotsSpinnerComponent,
    HasPermissionDirective,
    LowerCasePipe,
    TextClampTooltipDirective,
    DatePipe,
    BannerCardComponent,
    NgxSkeletonLoaderModule,
    DatatableComponent,
    IconComponent,
  ],
  providers: [AdminUserFeedbackStore, AdminUserGroupStore],
  selector: 'app-admin-user-feedback',
  styleUrl: './admin-user-feedback.component.scss',
  templateUrl: './admin-user-feedback.component.html',
})
export class AdminUserFeedbackComponent extends BaseComponent {
  public readonly typeOptions = Object.values(EBugReportType);
  public readonly statusOptions = Object.values(EBugReportStatus);

  public readonly searchedOnce = signal(true);

  public readonly rowData = computed(
    () =>
      this.store.userFeedback$()?.map((feedback) => ({
        createdAtDate: feedback.createdAt ? this.dateUtils.parseDateTimeFromApi(feedback.createdAt) : null,
        ...feedback,
        id: feedback.id ?? `feedback-${feedback.createdAt ?? Math.random()}`,
      })) ?? [],
  );
  public readonly total = computed(() => this.rowData().length);
  public readonly hasResults = computed(() => (this.store.userFeedback$()?.length ?? 0) > 0);
  public readonly showEmptyState = computed(() => this.searchedOnce() && !this.store.loading$() && !this.hasResults());

  public readonly datatableColumns: TDatatableColumn<IBugReport & { id: string }, string>[] = [
    {
      dataViewStrategy: (row) => row.type ?? '',
      id: 'type',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'shared.bugReport.type',
    },
    {
      dataViewStrategy: (row) => row.status ?? '',
      id: 'status',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'shared.status',
    },
    {
      dataViewStrategy: (row) => (row.createdAtDate ? row.createdAtDate.getTime() : 0),
      id: 'createdAt',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'shared.createdAt',
    },
    {
      id: 'userGroupId',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'shared.userGroupId',
    },
    {
      dataViewStrategy: (row) => row.message ?? '',
      id: 'message',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'shared.bugReport.message',
    },
    {
      dataViewStrategy: () => '',
      id: 'actions',
      isSortable: false,
    },
  ];

  public datatableConfig!: IDatatableConfiguration<IBugReport & { id: string }>;

  public readonly loaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        circleSize: '32px',
        className: 'w-[8%]',
        hasText: true,
        rowPadding: '16px 0',
        textWidth: '80px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        circleSize: '20px',
        className: 'w-[8%]',
        hasText: true,
        textWidth: '60px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        circleSize: '20px',
        className: 'w-[13%]',
        hasText: true,
        textWidth: '112px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        circleSize: '32px',
        className: 'w-[22%]',
        textWidth: '260px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        circleSize: '32px',
        className: 'w-[44%]',
        hasText: true,
        textWidth: '624px',
        type: 'text',
      },
      {
        borderRadius: '100vw',
        circleSize: '24px',
        className: 'w-[5%] flex justify-start',
        type: 'icon',
        width: '0',
      },
    ],
    rows: 5,
    showPaginator: true,
    showSearchField: true,
  };

  public selectedTypes: EBugReportType[] = [];
  public selectedStatuses: EBugReportStatus[] = [EBugReportStatus.new];
  public userGroupId = '';
  public selectedUserGroups: Set<string> = new Set();

  public readonly store = inject(AdminUserFeedbackStore);
  public readonly userGroupStore = inject(AdminUserGroupStore);
  private readonly sideDialogService = inject(SideDialogService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly tableStateService: TableStateService = inject(TableStateService);
  private readonly translate = inject(TranslateService);

  public readonly breadcrumbs: IBreadcrumb[] = [
    {
      label: this.translate.instant('pages.admin.userFeedback.navigationTitle'),
      link: '/dashboard',
    },
  ];

  constructor() {
    super();

    this.setDatatableConfig();
    this.store.fetchCreators();
    this.userGroupStore.fetchUserGroups();

    this.onSearchClick();
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      defaultSort: { active: 'createdAt', direction: 'desc' },
      isClientSide: true,
      isLoaderEnabled: true,
      isPaginationEnabled: true,
      isRowClickEnabled: true,
      isSearchEnabled: true,
      pageSizeOptions: [5, 10, 25, 50, 100],
      showNoDataText: true,
      skipInitialRequest: true,
      storageKey: this.tableStateService.buildKey('admin-user-feedback', 'table'),
    };
  }

  public onSearchClick(): void {
    this.searchedOnce.set(true);

    this.store.fetchUserFeedBackList({
      statuses: this.selectedStatuses.length ? this.selectedStatuses : undefined,
      types: this.selectedTypes.length ? this.selectedTypes : undefined,
      userGroupIds: this.selectedUserGroups.size ? Array.from(this.selectedUserGroups) : undefined,
    });
  }

  public onRowClick(feedback: IBugReport): void {
    this.sideDialogService.open(AdminUserFeedbackDetailsComponent, {
      data: {
        creator: this.resolveCreator(feedback),
        feedback,
      },
    });
  }

  public onMenuButtonClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  public onStatusSelect(event: MouseEvent, feedback: IBugReport, status: EBugReportStatus): void {
    event.stopPropagation();

    if (!feedback.id || feedback.status === status) {
      return;
    }

    this.store.updateUserFeedbackStatus({ id: feedback.id, status });
  }

  public onUserGroupChange(userGroupId: string): void {
    this.selectedUserGroups = new Set(userGroupId);
  }

  private resolveCreator(feedback: IBugReport): IUser | undefined {
    const users = this.store.users$();

    if (!users?.size) {
      return undefined;
    }

    if (feedback.createdBy && users.has(feedback.createdBy)) {
      return users.get(feedback.createdBy);
    }

    return undefined;
  }
}
